/**
 * AttachPickerModal — unified 4-tab attach picker for the RFI redesign
 * (Feb 2026 — Thread redesign).
 *
 * Industry-confirmed: the #1 RFI failure mode is the conversation being
 * separated from the drawings/clips/photos it references. This picker is
 * the antidote — one button on the RFI dialog opens this modal with four
 * tabs that pull from each project tool:
 *
 *   • Clips      → GET /api/clips?project_id (thumbnail + title + spec_num + room)
 *   • Drawings   → GET /api/projects/{id}/drawings (sheet thumb + number + title)
 *   • Documents  → GET /api/projects/{id}/files (project.documents[])
 *   • Upload     → camera/drag-drop → POST /projects/{id}/documents → attach
 *   • Palettes   → GET /api/attachables (RS-3a — swatch strip + name)
 *   • Boards     → GET /api/attachables (RS-3a — Mood Boards and Yard tables)
 *
 * Picking returns an RFIAttachment shape:
 *   { kind, ref_id, label, thumbnail_url, file_url, metadata }
 *
 * The same modal is reused in the Create RFI dialog, the Thread reply box and
 * the Scans panel (SC-5 — which asks for three tabs and PDFs or images only).
 * `tabs`, `title`, `description` and `accept` narrow it; by default it is the
 * full six-tab picker it has always been. Mobile-first: tabs are big, items are scrollable, no horizontal
 * overflow in iPhone portrait.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, Upload, FileText, Image as ImageIcon, Layers, Pen, Check, Palette, LayoutGrid } from 'lucide-react';
import { SwatchStrip } from './AttachmentChipParts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_META = {
  clip:     { label: 'Clip',     icon: Layers,    accent: '#70170F' },
  drawing:  { label: 'Drawing',  icon: Pen,       accent: '#1E40AF' },
  document: { label: 'Document', icon: FileText,  accent: '#475569' },
  image:    { label: 'Photo',    icon: ImageIcon, accent: '#047857' },
  file:     { label: 'File',     icon: FileText,  accent: '#475569' },
};

// RS-3a — the two new kinds wear accents this file already carries.
KIND_META.palette = { label: 'Palette', icon: Palette, accent: KIND_META.document.accent };
KIND_META.board = { label: 'Board', icon: LayoutGrid, accent: KIND_META.drawing.accent };

const CARD_SKIN = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded p-2 hover:border-[#70170F] transition-colors';

const ALL_TABS = ['clips', 'drawings', 'documents', 'palettes', 'boards', 'upload'];

// Tailwind needs the class written out, so the few counts we use are spelled.
const TAB_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-2 sm:grid-cols-4' };

/** SC-5 — the project documents rail, on its own so a drop or a paste
 *  elsewhere in the app can use the same one. */
export async function uploadProjectDocument(projectId, file, isPhoto = false) {
  const form = new FormData();
  form.append('file', file);
  form.append('category', isPhoto ? 'photo' : 'other');
  form.append('description', '');
  const r = await axios.post(`${API}/projects/${projectId}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data?.document || null;
}

/** SC-5 — is this name a PDF or a picture? */
export const isPlanFile = (name) => /\.(pdf|png|jpe?g|webp|gif|heic)(\?|$)/i.test(String(name || ''));

export default function AttachPickerModal({
  open,
  onOpenChange,
  projectId,
  alreadyAttachedIds = [],   // [ref_id...] — picker grays these out
  onAttach,                  // (attachment) => void
  tabs = ALL_TABS,           // SC-5 — which tabs this caller wants
  title = 'Attach to RFI',
  description = "Pull from the project's clips, drawings, documents, palettes and boards — or upload / snap a photo.",
  accept,                    // SC-5 — (name) => boolean, when only some files will do
}) {
  const shown = useMemo(() => ALL_TABS.filter(t => tabs.includes(t)), [tabs]);
  const [tab, setTab] = useState(shown[0] || 'clips');
  const [search, setSearch] = useState('');
  const [clips, setClips] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [files, setFiles] = useState([]);
  const [palettes, setPalettes] = useState([]);
  const [boards, setBoards] = useState([]);
  const [preparing, setPreparing] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => { if (open) setTab(shown[0] || 'clips'); }, [open, shown]);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    Promise.allSettled([
      axios.get(`${API}/clips?project_id=${projectId}`),
      axios.get(`${API}/projects/${projectId}/drawings`),
      axios.get(`${API}/projects/${projectId}/files`),
      axios.get(`${API}/attachables?project_id=${projectId}`),
    ]).then(([clipsR, drawingsR, filesR, attachablesR]) => {
      const clipsList = clipsR.status === 'fulfilled'
        ? (Array.isArray(clipsR.value.data) ? clipsR.value.data : (clipsR.value.data?.clips || []))
        : [];
      const drawingsList = drawingsR.status === 'fulfilled'
        ? (Array.isArray(drawingsR.value.data) ? drawingsR.value.data : (drawingsR.value.data?.drawings || []))
        : [];
      const filesList = filesR.status === 'fulfilled'
        ? (Array.isArray(filesR.value.data) ? filesR.value.data : (filesR.value.data?.files || []))
        : [];
      setClips(clipsList);
      setDrawings(drawingsList);
      setFiles(filesList);
      // RS-3a — the firm's palettes and boards, this project's first.
      const attachables = attachablesR.status === 'fulfilled' ? attachablesR.value.data : {};
      setPalettes(attachables?.palettes || []);
      setBoards(attachables?.boards || []);
    }).finally(() => setLoading(false));
  }, [open, projectId]);

  const attachedSet = useMemo(() => new Set(alreadyAttachedIds), [alreadyAttachedIds]);

  const handlePick = (attachment) => {
    if (attachedSet.has(attachment.ref_id)) return;
    onAttach?.(attachment);
    toast.success(`${KIND_META[attachment.kind]?.label || 'Item'} attached`);
  };

  // RS-3a — a palette or a board is turned into an attachment by the server,
  // which mints the view-only share token and the public palette code once.
  const pickPrepared = async (kind, id) => {
    if (attachedSet.has(id) || preparing) return;
    setPreparing(id);
    try {
      const r = await axios.post(`${API}/rfi-attachments/prepare`, { kind, id });
      handlePick(r.data);
    } catch (e) {
      console.error('[AttachPicker] prepare failed:', e);
      toast.error(kind === 'palette' ? 'Could not attach that palette' : 'Could not attach that board');
    } finally {
      setPreparing('');
    }
  };

  // --- file upload (Upload tab + Camera button) ---
  const uploadFile = async (file, isPhoto) => {
    if (!file || !projectId) return;
    setUploading(true);
    try {
      const doc = await uploadProjectDocument(projectId, file, isPhoto);
      if (doc) {
        // Auto-attach the freshly uploaded file.
        handlePick({
          kind: isPhoto ? 'image' : 'document',
          ref_id: doc.id,
          label: doc.original_name || file.name,
          thumbnail_url: doc.thumbnail_url || null,
          file_url: doc.file_url || null,
          metadata: { project_id: projectId, category: doc.category, file_type: doc.file_type },
        });
        // Add to the Documents tab list too.
        setFiles(f => [doc, ...f]);
      } else {
        toast.error('Upload succeeded but no document returned');
      }
    } catch (e) {
      console.error('[AttachPicker] upload failed:', e);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const filteredClips = useMemo(() => {
    if (!search) return clips;
    const q = search.toLowerCase();
    return clips.filter(c =>
      (c.title || '').toLowerCase().includes(q)
      || (c.spec_num || '').toLowerCase().includes(q)
      || (c.room || '').toLowerCase().includes(q)
      || (c.manufacturer || '').toLowerCase().includes(q),
    );
  }, [clips, search]);

  const filteredDrawings = useMemo(() => {
    const takeable = accept
      ? drawings.filter(d => accept(d.current_file_url || d.title || ''))
      : drawings;
    if (!search) return takeable;
    const q = search.toLowerCase();
    return takeable.filter(d =>
      (d.drawing_number || '').toLowerCase().includes(q)
      || (d.title || '').toLowerCase().includes(q)
      || (d.discipline || '').toLowerCase().includes(q),
    );
  }, [drawings, search, accept]);

  const filteredFiles = useMemo(() => {
    const takeable = accept ? files.filter(f => accept(f.original_name || '')) : files;
    if (!search) return takeable;
    const q = search.toLowerCase();
    return takeable.filter(f =>
      (f.original_name || '').toLowerCase().includes(q)
      || (f.category || '').toLowerCase().includes(q),
    );
  }, [files, search, accept]);

  const filteredPalettes = useMemo(() => {
    if (!search) return palettes;
    const q = search.toLowerCase();
    return palettes.filter(p => (p.name || '').toLowerCase().includes(q)
      || (p.swatches || []).some(s => `${s.name} ${s.code}`.toLowerCase().includes(q)));
  }, [palettes, search]);

  const filteredBoards = useMemo(() => {
    if (!search) return boards;
    const q = search.toLowerCase();
    return boards.filter(b => (b.name || '').toLowerCase().includes(q)
      || (b.board_mode || '').toLowerCase().includes(q));
  }, [boards, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0F0F0F] border-[#2A2A2A] text-white max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="attach-picker-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
          <DialogDescription className="text-gray-400">{description}</DialogDescription>
        </DialogHeader>

        <Input
          data-testid="attach-picker-search"
          placeholder="Search across all tabs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
        />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className={`grid ${TAB_COLS[shown.length] || 'grid-cols-3 sm:grid-cols-6'} bg-[#1A1A1A] h-auto`}>
            {shown.includes('clips') && (
            <TabsTrigger value="clips" data-testid="attach-picker-tab-clips">
              <Layers className="w-4 h-4 mr-1" /> Clips
              <span className="ml-1 text-xs text-gray-500">({filteredClips.length})</span>
            </TabsTrigger>)}
            {shown.includes('drawings') && (
            <TabsTrigger value="drawings" data-testid="attach-picker-tab-drawings">
              <Pen className="w-4 h-4 mr-1" /> Drawings
              <span className="ml-1 text-xs text-gray-500">({filteredDrawings.length})</span>
            </TabsTrigger>)}
            {shown.includes('documents') && (
            <TabsTrigger value="documents" data-testid="attach-picker-tab-documents">
              <FileText className="w-4 h-4 mr-1" /> Documents
              <span className="ml-1 text-xs text-gray-500">({filteredFiles.length})</span>
            </TabsTrigger>)}
            {shown.includes('palettes') && (
            <TabsTrigger value="palettes" data-testid="attach-picker-tab-palettes">
              <Palette className="w-4 h-4 mr-1" /> Palettes
              <span className="ml-1 text-xs text-gray-500">({filteredPalettes.length})</span>
            </TabsTrigger>)}
            {shown.includes('boards') && (
            <TabsTrigger value="boards" data-testid="attach-picker-tab-boards">
              <LayoutGrid className="w-4 h-4 mr-1" /> Boards
              <span className="ml-1 text-xs text-gray-500">({filteredBoards.length})</span>
            </TabsTrigger>)}
            {shown.includes('upload') && (
            <TabsTrigger value="upload" data-testid="attach-picker-tab-upload">
              <Upload className="w-4 h-4 mr-1" /> Upload
            </TabsTrigger>)}
          </TabsList>

          <TabsContent value="clips" className="mt-3">
            {loading ? (
              <div className="text-sm text-gray-500 p-4 text-center">Loading…</div>
            ) : filteredClips.length === 0 ? (
              <div className="text-sm text-gray-500 p-6 text-center">No clips on this project yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredClips.map(c => (
                  <PickerCard
                    key={c.id}
                    kind="clip"
                    label={c.title || c.name || 'Untitled clip'}
                    subtitle={[c.spec_num, c.room].filter(Boolean).join(' · ') || c.manufacturer || ''}
                    thumb={c.primary_image || c.images?.[0]}
                    disabled={attachedSet.has(c.id)}
                    onClick={() => handlePick({
                      kind: 'clip',
                      ref_id: c.id,
                      label: c.title || c.name || 'Clip',
                      thumbnail_url: c.primary_image || c.images?.[0] || null,
                      file_url: null,
                      metadata: { spec_num: c.spec_num, room: c.room, manufacturer: c.manufacturer },
                    })}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="drawings" className="mt-3">
            {loading ? (
              <div className="text-sm text-gray-500 p-4 text-center">Loading…</div>
            ) : filteredDrawings.length === 0 ? (
              <div className="text-sm text-gray-500 p-6 text-center">No drawings on this project yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredDrawings.map(d => (
                  <PickerCard
                    key={d.id}
                    kind="drawing"
                    label={`${d.drawing_number || ''} ${d.title || ''}`.trim() || 'Untitled drawing'}
                    subtitle={d.discipline || ''}
                    thumb={d.current_file_url}
                    disabled={attachedSet.has(d.id)}
                    onClick={() => handlePick({
                      kind: 'drawing',
                      ref_id: d.id,
                      label: `${d.drawing_number || ''} ${d.title || ''}`.trim() || 'Drawing',
                      thumbnail_url: d.current_file_url || null,
                      file_url: d.current_file_url || null,
                      metadata: { drawing_number: d.drawing_number, discipline: d.discipline },
                    })}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="documents" className="mt-3">
            {loading ? (
              <div className="text-sm text-gray-500 p-4 text-center">Loading…</div>
            ) : filteredFiles.length === 0 ? (
              <div className="text-sm text-gray-500 p-6 text-center">No files on this project yet. Use the Upload tab.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredFiles.map(f => (
                  <PickerCard
                    key={f.id}
                    kind={(f.file_type === 'png' || f.file_type === 'jpg' || f.file_type === 'jpeg' || f.file_type === 'webp') ? 'image' : 'document'}
                    label={f.original_name || 'Untitled'}
                    subtitle={[f.category, f.file_type].filter(Boolean).join(' · ')}
                    thumb={f.thumbnail_url}
                    disabled={attachedSet.has(f.id)}
                    onClick={() => handlePick({
                      kind: (f.file_type === 'png' || f.file_type === 'jpg' || f.file_type === 'jpeg' || f.file_type === 'webp') ? 'image' : 'document',
                      ref_id: f.id,
                      label: f.original_name || 'File',
                      thumbnail_url: f.thumbnail_url || null,
                      file_url: f.file_url || null,
                      metadata: { project_id: projectId, category: f.category, file_type: f.file_type },
                    })}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="palettes" className="mt-3">
            {loading ? (
              <div className="text-sm text-gray-500 p-4 text-center">Loading…</div>
            ) : filteredPalettes.length === 0 ? (
              <div className="text-sm text-gray-500 p-6 text-center">No palettes yet. Build one in the Palette tool.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredPalettes.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`attach-picker-option-${p.id}`}
                    disabled={attachedSet.has(p.id) || preparing === p.id}
                    onClick={() => pickPrepared('palette', p.id)}
                    className={`flex items-center gap-3 text-left ${CARD_SKIN} ${attachedSet.has(p.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <SwatchStrip swatches={p.swatches} testId={`attach-picker-swatches-${p.id}`} />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium text-white truncate">{p.name}</span>
                      <span className="block text-xs text-gray-500">
                        {`${p.color_count} colour${p.color_count === 1 ? '' : 's'}`}
                        {p.project_id === projectId ? ' · this project' : ''}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="boards" className="mt-3">
            {loading ? (
              <div className="text-sm text-gray-500 p-4 text-center">Loading…</div>
            ) : filteredBoards.length === 0 ? (
              <div className="text-sm text-gray-500 p-6 text-center">No mood boards or Dreamcasting tables yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredBoards.map(b => (
                  <PickerCard
                    key={b.id}
                    kind="board"
                    optionId={b.id}
                    label={b.name}
                    subtitle={`${b.board_mode}${b.project_id === projectId ? ' · this project' : ''}`}
                    thumb={b.thumbnail_url}
                    mark={(b.name || 'B').trim().charAt(0).toUpperCase()}
                    disabled={attachedSet.has(b.id) || preparing === b.id}
                    onClick={() => pickPrepared('board', b.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="mt-3">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  data-testid="attach-picker-upload-file-btn"
                  variant="outline"
                  className="border-[#2A2A2A] h-24 flex flex-col"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-6 h-6 mb-1" />
                  <span className="text-xs">Upload file</span>
                </Button>
                <Button
                  type="button"
                  data-testid="attach-picker-camera-btn"
                  variant="outline"
                  className="border-[#2A2A2A] h-24 flex flex-col"
                  disabled={uploading}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="w-6 h-6 mb-1" />
                  <span className="text-xs">Take photo</span>
                </Button>
              </div>
              {uploading && <div className="text-xs text-gray-500 text-center">Uploading…</div>}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], false)}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], true)}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#2A2A2A]"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PickerCard({ kind, label, subtitle, thumb, disabled, onClick, mark, optionId }) {
  const meta = KIND_META[kind] || KIND_META.file;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      data-testid={`attach-picker-option-${optionId || kind}`}
      disabled={disabled}
      onClick={onClick}
      className={`text-left ${CARD_SKIN} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className="relative w-full h-20 bg-[#0F0F0F] rounded mb-2 overflow-hidden flex items-center justify-center">
        {thumb ? (
          <img src={thumb} alt={label} className="w-full h-full object-cover" />
        ) : mark ? (
          <span className="text-2xl font-semibold" style={{ color: meta.accent }}>{mark}</span>
        ) : (
          <Icon className="w-8 h-8" style={{ color: meta.accent }} />
        )}
        {disabled && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Check className="w-6 h-6 text-green-400" />
          </div>
        )}
      </div>
      <div className="text-xs font-medium text-white truncate">{label}</div>
      {subtitle && <div className="text-[10px] text-gray-500 truncate">{subtitle}</div>}
      <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: meta.accent }}>{meta.label}</div>
    </button>
  );
}
